import * as React from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCog, FaClock, FaBan } from 'react-icons/fa';
import ShiftApplicationIndicator, { Shift as BaseShift, ShiftApplication, EmployeeWeekStat } from './ShiftApplicationIndicator';
import ShiftSwapIndicator from './ShiftSwapIndicator';
import ShiftDetailsModal from './ShiftDetailsModal';
import { SwapRequestMinimal } from '../features/shifts/services/SwapService';
import { colors } from '../features/employees/styles/personalakteStyles';
import dateUtil from '../utils/dateUtil';

// Employee interface for assigned employees
export interface Employee {
  id: number;
  first_name: string;
  last_name: string;
}

// Extended Shift interface with all properties used in ShiftBlock
export interface Shift extends BaseShift {
  id: number;
  start_time: string;
  end_time: string;
  assigned_employees?: Employee[];
  employees_needed?: number;
  work_area__name?: string;
  work_area__color?: string;
  work_area?: string | number;
  employee?: number;
  employee_name?: string;
  applications?: ShiftApplication[];
  catering_notes?: string;
  notes?: string;
}

// Employee with photo for applications
export interface EmployeeWithPhoto {
  id: number;
  first_name: string;
  last_name: string;
  photo_url?: string;
}

// Props interface for ShiftBlock component
export interface ShiftBlockProps {
  shift: Shift | null | undefined;
  onShiftClick: (shiftId: number) => void;
  onEditClick: (shift: Shift) => void;
  activeEmployeeId: number | null;
  onLongPress?: (ev: React.MouseEvent | React.TouchEvent) => void;
  disableClicks?: boolean;
  isActiveEmployeeAbsent?: boolean;
  onAssignClick?: (shiftId: number, employeeId: number) => void;
  allEmployees?: EmployeeWithPhoto[];
  swapRequests?: SwapRequestMinimal[];
  employeeStats?: Record<number, EmployeeWeekStat>;
}

function formatTime(isoString: string | null | undefined): string {
  if (!isoString) return '--:--';
  const dt = dateUtil.convertUTCToTenantTime(isoString) || new Date(isoString);
  const hh = dt.getHours().toString().padStart(2, '0');
  const mm = dt.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

export default function ShiftBlock({
  shift,
  onShiftClick,
  onEditClick,
  activeEmployeeId,
  onLongPress,
  disableClicks,
  isActiveEmployeeAbsent = false,
  onAssignClick,
  allEmployees = [],
  swapRequests = [],
  employeeStats = {}
}: ShiftBlockProps): React.ReactElement | null {
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const blockRef = React.useRef<HTMLDivElement>(null);
  const longPressTimer = React.useRef<number | null>(null);
  const longPressTriggered = React.useRef<boolean>(false);
  const startPos = React.useRef<{x:number;y:number}>({x:0,y:0});
  
  if (!shift) return null;

  const assigned: Employee[] = shift.assigned_employees || [];
  const needed: number = shift.employees_needed || 1;
  const countAssigned: number = assigned.length;

  // Hintergrundfarbe
  let backgroundColor: string = '#f9f9f9';
  if (countAssigned > 0) {
    backgroundColor = shift.work_area__color || '#c8f7c5';
  }

  // Fettere Umrandung, wenn der aktuell aktive Employee in dieser Schicht steckt
  const isActive: boolean = assigned.some(e => e.id === activeEmployeeId);

  // Hat der aktive Mitarbeiter eine Bewerbung auf diese Schicht?
  const hasApplicationByActiveEmployee: boolean = (() => {
    if (!activeEmployeeId || !shift.applications || shift.applications.length === 0) return false;
    const activeIdStr = String(activeEmployeeId);
    return shift.applications.some(app => (app.status === 'APPLIED' || app.status === 'PENDING') && String(app.employee) === activeIdStr);
  })();

  let borderStyle: string = `1px solid ${colors.border}`;
  if (isActive) {
    borderStyle = '3px solid black';
  } else if (hasApplicationByActiveEmployee) {
    borderStyle = `3px dashed ${colors.navy}`;
  }

  // Klick-Handler: Single click => Details Modal öffnen ODER Assignment
  const handleClick = (): void => {
    if (disableClicks) return;
    if (longPressTriggered.current) {
      // Verhindere normalen Klick direkt nach Long-Press
      longPressTriggered.current = false;
      return;
    }
    setShowDetailsModal(true);
  };

  // Doppel-Klick => SHIFT Edit (Fallback)
  const handleDoubleClick = (): void => {
    if (disableClicks) return;
    if (onEditClick) {
      onEditClick(shift);
    }
  };

  const resetHoverStyles = (): void => {
    if (blockRef.current) {
      blockRef.current.style.transform = 'translateY(0) scale(1)';
      blockRef.current.style.boxShadow = getBoxShadow();
    }
  };

  const handleCloseDetailsModal = (): void => {
    setShowDetailsModal(false);
    resetHoverStyles();
  };

  // Zahnrad => SHIFT Edit, ohne Modal
  const handleGearClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (disableClicks) return;
    if (onEditClick) {
      onEditClick(shift);
    }
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const getEventPoint = (ev: any) => {
    if (ev.touches && ev.touches[0]) return { x: ev.touches[0].clientX, y: ev.touches[0].clientY };
    return { x: ev.clientX, y: ev.clientY };
  };

  const onDown = (ev: React.MouseEvent | React.TouchEvent) => {
    if (!onLongPress) return;
    const pt = getEventPoint(ev);
    startPos.current = { x: pt.x, y: pt.y };
    clearLongPress();
    longPressTimer.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      onLongPress(ev);
    }, 350);
  };

  const onMove = (ev: React.MouseEvent | React.TouchEvent) => {
    if (!onLongPress || longPressTimer.current === null) return;
    const pt = getEventPoint(ev);
    const dx = Math.abs(pt.x - startPos.current.x);
    const dy = Math.abs(pt.y - startPos.current.y);
    if (dx > 6 || dy > 6) {
      // zu viel Bewegung => LongPress abbrechen
      clearLongPress();
    }
  };

  const onUp = () => {
    clearLongPress();
  };

  const startStr: string = formatTime(shift.start_time);
  const endStr: string = formatTime(shift.end_time);
  const waName: string = shift.work_area__name || '—';

  // Namen der zugeteilten Mitarbeiter (kompakt für breitere Blöcke)
  const getAssignedNamesData = () => {
    if (assigned.length === 0) return { display: '—', full: '—', isTruncated: false };

    const names = assigned.map(emp => {
            const first: string = emp.first_name || '';
            const lastI: string = emp.last_name ? ` ${emp.last_name[0]}.` : '';
            return first + lastI;
    });

    const fullText = names.join(', ');
    return { display: fullText, full: fullText, isTruncated: false };
  };

  const assignedNamesData = getAssignedNamesData();
  const assignedNames: string = assignedNamesData.display;

  // Notes-Funktionalität
  // Notes-Funktionalität mit Backend-Daten
  const hasNotes: boolean = Boolean(shift.notes || shift.catering_notes);
  const fullNotes: string = shift.notes || shift.catering_notes || '';
  
  // Kompakte Vorschau für ShiftBlocks: Maximal ~30 Zeichen
  const getNotesPreview = (text: string): string => {
    if (!text) return '';
    const singleLine = text.split('\n')[0].replace(/\r|\n/g, ' ').trim();
    if (singleLine.length <= 30) {
      return singleLine;
    }
    return singleLine.substring(0, 27) + '...';
  };

  const notesPreview: string = getNotesPreview(fullNotes);

  const accentColor = shift.work_area__color || '#5271A5';

  // Hex zu RGB für Glow-Effekte
  const hexToRgb = (hex: string) => {
    const cleanHex = hex.replace('#', '');
    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16)
    };
  };

  const accentRgb = hexToRgb(accentColor);

  const getGlassBackground = (): string => {
    if (isActiveEmployeeAbsent) {
      return 'rgba(239, 68, 68, 0.12)';
    }
    // Unbesetzte Schichten: transparent (nur Rand sichtbar)
    if (countAssigned === 0) {
      return 'transparent';
    }
    return `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.15)`;
  };

  const glassBackground = getGlassBackground();

  const getBorderStyle = (): string => {
    if (isActiveEmployeeAbsent) return '2px solid rgba(239, 68, 68, 0.5)';
    if (isActive) return `2px solid rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.7)`;
    if (hasApplicationByActiveEmployee) return '2px solid #86efac';
    // Unbesetzte Schichten: leichter Work Area Rand
    if (countAssigned === 0) return `1px solid rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.3)`;
    return `1px solid rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.2)`;
  };

  const getBoxShadow = (): string => {
    if (isActiveEmployeeAbsent) return '0 2px 8px rgba(239, 68, 68, 0.15)';
    if (isActive) return `0 4px 20px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.45), 0 0 15px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.35), inset 0 1px 0 rgba(255,255,255,0.3)`;
    if (hasApplicationByActiveEmployee) return '0 0 12px rgba(34, 197, 94, 0.5), 0 4px 16px rgba(34, 197, 94, 0.4)';
    // Immer einen subtilen Glow mit der WA-Farbe zeigen
    return `0 2px 10px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.15), 0 4px 12px rgba(0, 0, 0, 0.04)`;
  };

  const isFull = countAssigned >= needed;

  // Dämpfe Schichten ab, wenn ein MA ausgewählt ist, aber nicht dieser Schicht zugewiesen
  const isDimmed = activeEmployeeId !== null && !isActive && !hasApplicationByActiveEmployee;

  const getOpacity = (): number => {
    if (isActiveEmployeeAbsent) return 0.7;
    if (isDimmed) return 0.7;
    return 1;
  };

  return (
    <div
      ref={blockRef}
      data-testid={`shift-block-${shift.id}`}
      style={{
        marginBottom: '8px',
        padding: 'clamp(4px, 6cqi, 8px) clamp(4px, 8cqi, 10px)',
        background: glassBackground,
        borderRadius: '10px',
        cursor: 'pointer',
        position: 'relative',
        width: '100%',
        minHeight: '80px',
        height: 'auto',
        boxSizing: 'border-box',
        containerType: 'inline-size',
        border: getBorderStyle(),
        boxShadow: isDimmed ? 'none' : getBoxShadow(),
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.3s ease',
        overflow: 'hidden',
        opacity: getOpacity(),
        '--pulse-r': accentRgb.r,
        '--pulse-g': accentRgb.g,
        '--pulse-b': accentRgb.b,
        animation: isActive ? 'shift-block-pulse 2s ease-in-out 5 forwards' : 'none',
      } as React.CSSProperties}
      onMouseDown={onDown as any}
      onMouseMove={onMove as any}
      onMouseUp={onUp as any}
      onTouchStart={onDown as any}
      onTouchMove={onMove as any}
      onTouchEnd={onUp as any}
      onClick={(e) => {
        e.stopPropagation();
        if (activeEmployeeId) {
          onShiftClick(shift.id);
        } else {
          handleClick();
        }
      }}
      onDoubleClick={(e) => { e.stopPropagation(); handleDoubleClick(); }}
      onMouseEnter={(e) => {
        if (!isActiveEmployeeAbsent) {
          e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
          e.currentTarget.style.boxShadow = `0 8px 30px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.5), 0 0 20px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.35), inset 0 1px 0 rgba(255,255,255,0.4)`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = getBoxShadow();
      }}
    >
      {/* Farbiger Akzent-Balken links - Work Area Farbe, dünner wenn unbesetzt */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: isActive ? '5px' : countAssigned > 0 ? '4px' : '3px',
          background: isActiveEmployeeAbsent
            ? '#ef4444'
            : countAssigned === 0
              ? `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.5)`
              : `linear-gradient(180deg, rgba(${Math.min(255, accentRgb.r + 30)}, ${Math.min(255, accentRgb.g + 30)}, ${Math.min(255, accentRgb.b + 30)}, 1) 0%, ${accentColor} 50%, rgba(${Math.max(0, accentRgb.r - 20)}, ${Math.max(0, accentRgb.g - 20)}, ${Math.max(0, accentRgb.b - 20)}, 1) 100%)`,
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px',
          boxShadow: isActive
            ? `0 0 12px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.7), 0 0 20px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.4)`
            : countAssigned > 0
              ? `0 0 8px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.4)`
              : 'none',
        }}
      />

      {/* Zahnrad rechts oben */}
      <div
        data-testid="edit-icon"
        onClick={handleGearClick}
        style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          cursor: 'pointer',
          width: '16px',
          height: '16px',
          borderRadius: '4px',
          background: 'rgba(255, 255, 255, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FaCog
          data-testid="gear-icon"
          style={{
            fontSize: '8px',
            color: colors.textMuted
          }}
        />
      </div>

      {/* Blocked Badge */}
      {isActiveEmployeeAbsent && (
        <div
          style={{
            position: 'absolute',
            top: '5px',
            left: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
          }}
        >
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Mitarbeiter abwesend"
          >
            <FaBan style={{ fontSize: '7px', color: '#ef4444' }} />
          </div>
        </div>
      )}

      {/* WorkArea oben */}
      <div
        data-testid="work-area-name"
        style={{
          fontWeight: 700,
          marginBottom: '4px',
          fontSize: 'clamp(8px, 9cqi, 11px)',
          lineHeight: '1.2',
          color: isActiveEmployeeAbsent ? colors.textLight : colors.textPrimary,
          paddingRight: '22px',
          paddingLeft: isActiveEmployeeAbsent ? '20px' : '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {waName}
      </div>

      {/* Zeiten + MA-Anzahl auf gleicher Höhe */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '4px',
          paddingRight: '0',
          marginRight: '-8px',
          flexWrap: 'wrap',
          gap: '2px',
        }}
      >
        <div
          data-testid="time-range"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 'clamp(7px, 8cqi, 10px)',
            background: 'rgba(255, 255, 255, 0.5)',
            padding: '1px clamp(2px, 4cqi, 6px)',
            borderRadius: '4px',
            color: isActiveEmployeeAbsent ? colors.textLight : colors.textBody,
            fontWeight: 500,
            textDecoration: isActiveEmployeeAbsent ? 'line-through' : 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {startStr} - {endStr}
        </div>
        <span
          data-testid="assigned-info"
          style={{
            padding: '2px 6px',
            borderRadius: '4px',
            background: isFull
              ? 'rgba(34, 197, 94, 0.12)'
              : `${colors.dangerLight}`,
            color: isFull ? '#15803d' : colors.danger,
            fontWeight: 500,
            fontSize: 'clamp(7px, 8cqi, 10px)',
            lineHeight: '1',
          }}
        >
          {countAssigned}/{needed}
        </span>
      </div>

      {/* Zugeordnete Mitarbeiter */}
      <div
        data-testid="assigned-names"
        style={{
          fontSize: 'clamp(7px, 8cqi, 10px)',
          lineHeight: '1.2',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: assigned.length > 0 ? colors.textBody : colors.textLight,
          fontWeight: assigned.length > 0 ? 500 : 400,
        }}
      >
        {assignedNames}
      </div>

      {/* Notes-Zeile */}
      <div
        data-testid="notes-line"
        style={{
          fontSize: 'clamp(6px, 7cqi, 9px)',
          color: colors.textMuted,
          fontStyle: 'italic',
          marginTop: '3px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          visibility: hasNotes ? 'visible' : 'hidden',
        }}
      >
        {hasNotes ? `"${notesPreview}"` : '\u00A0'}
      </div>

      {/* Swap Indicator (above application indicator) */}
      <ShiftSwapIndicator
        shiftId={shift.id}
        swapRequests={swapRequests}
        allEmployees={allEmployees}
        accentColor={accentColor}
        assignedEmployees={assigned}
      />

      {/* Application Indicator (bottom-right) */}
      <ShiftApplicationIndicator
        shift={shift}
        onAssignClick={onAssignClick}
        accentColor={accentColor}
        allEmployees={allEmployees}
        employeeStats={employeeStats}
      />

      {/* Shift Details Modal - portaled to body to escape backdrop-filter containing block */}
      {showDetailsModal && createPortal(
        <ShiftDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          onEdit={() => {
            handleCloseDetailsModal();
            if (onEditClick) {
              onEditClick(shift);
            }
          }}
          shift={shift}
        />,
        document.body
      )}
    </div>
  );
} 